/**
 * Camera glyphs for the Mapbox camera layer.
 *
 * Device tracks and dwells are circles, so cameras use a distinct video-camera
 * glyph instead. One image per category (tinted) plus a neutral cluster glyph
 * are rasterised on a canvas and registered with the map; `styleimagemissing`
 * re-adds them after a style swap or for unknown category slugs.
 */
import type { Map as MapboxMap } from 'mapbox-gl';
import { CAMERA_CATEGORY_COLORS, CAMERA_OVERLAY_COLOR } from '../services/cameraOverlayService';

export const CAMERA_ICON_PREFIX = 'camera-icon-';
export const CAMERA_CLUSTER_ICON = `${CAMERA_ICON_PREFIX}cluster`;

/** Base glyph size in CSS px at icon-size 1 (rendered at 2x for HiDPI). */
export const CAMERA_ICON_BASE_PX = 24;
const PIXEL_RATIO = 2;

/** Material "Videocam" glyph, 24x24 viewBox. */
const VIDEOCAM_PATH =
  'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z';

export function cameraIconId(category?: string | null): string {
  return `${CAMERA_ICON_PREFIX}${category || 'other'}`;
}

function colorForIconId(id: string): string {
  if (id === CAMERA_CLUSTER_ICON) return CAMERA_OVERLAY_COLOR;
  const slug = id.slice(CAMERA_ICON_PREFIX.length);
  return CAMERA_CATEGORY_COLORS[slug] || CAMERA_OVERLAY_COLOR;
}

/** Rasterise the glyph: white outline for contrast on any basemap, category fill. */
export function renderCameraIcon(color: string, opts: { outline?: string; sizePx?: number } = {}): ImageData | null {
  if (typeof document === 'undefined') return null;
  const sizePx = opts.sizePx ?? CAMERA_ICON_BASE_PX;
  const px = sizePx * PIXEL_RATIO;
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx || typeof Path2D === 'undefined') return null;

  // Leave a 2px (CSS) margin so the outline isn't clipped.
  const margin = 2 * PIXEL_RATIO;
  const scale = (px - margin * 2) / 24;
  ctx.translate(margin, margin);
  ctx.scale(scale, scale);
  const path = new Path2D(VIDEOCAM_PATH);

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  // Line widths are in path units; divide by `scale` so they come out in device px.
  ctx.strokeStyle = opts.outline ?? 'rgba(255,255,255,0.95)';
  ctx.lineWidth = 3 / scale; // 3 device px ≈ 1.5 CSS px outline
  ctx.stroke(path);
  // Thin dark edge between fill and outline keeps it readable on light basemaps.
  ctx.strokeStyle = 'rgba(0,0,0,0.4)';
  ctx.lineWidth = 1.2 / scale;
  ctx.stroke(path);
  ctx.fillStyle = color;
  ctx.fill(path);

  return ctx.getImageData(0, 0, px, px);
}

function addIcon(map: MapboxMap, id: string): void {
  if (map.hasImage(id)) return;
  const img = renderCameraIcon(colorForIconId(id), id === CAMERA_CLUSTER_ICON ? { sizePx: 28 } : undefined);
  if (!img) return;
  try {
    map.addImage(id, img, { pixelRatio: PIXEL_RATIO });
  } catch {
    /* image raced with a style reload; styleimagemissing will retry */
  }
}

/** Register every known category glyph plus the cluster glyph (idempotent). */
export function ensureCameraIcons(map: MapboxMap): void {
  Object.keys(CAMERA_CATEGORY_COLORS).forEach((slug) => addIcon(map, cameraIconId(slug)));
  addIcon(map, cameraIconId('other'));
  addIcon(map, CAMERA_CLUSTER_ICON);
}

/**
 * Hook the map so camera icons survive style changes and unknown slugs.
 * Returns a disposer.
 */
export function installCameraIcons(map: MapboxMap): () => void {
  const onMissing = (e: { id: string }) => {
    if (e.id && e.id.startsWith(CAMERA_ICON_PREFIX)) addIcon(map, e.id);
  };
  const onStyle = () => ensureCameraIcons(map);
  map.on('styleimagemissing', onMissing);
  map.on('style.load', onStyle);
  if (map.isStyleLoaded()) ensureCameraIcons(map);
  return () => {
    map.off('styleimagemissing', onMissing);
    map.off('style.load', onStyle);
  };
}
